/**
 * use-crop-modal-handlers
 *
 * All event handler callbacks for the crop modal.
 * Reads from context and dispatches actions or calls create/update hooks.
 */

import { useCallback } from 'react'
import _ from 'lodash'
import {
  useCropModalContext,
  useCropModalContextDispatch,
  setSpecies,
  setVariety,
  setCount,
  setGrowthType,
  setLifecycleType,
  setSelectedCollections,
  togglePhaseCode,
  toggleTaskCode,
  setAdjustedPhases,
  setAdjustedTasks,
  setUserOrg,
  setShowNotes,
  setSuccessions,
  enqueueCrop,
  resetForm,
  resetCropModal,
  setCustomCropMode,
  setCustomSpeciesName,
  setCustomVarietyMode,
  setCustomVarietyName,
  setCustomFamilyCode,
  setArchetype,
} from '../context/crop-modal-context'
import { handleStratificationToggle, handleDecompositionToggle, handleDormantToggle, computePhasesAfterToggle } from '../utils/crop-phase-utils'
import { parseIntervalToDays } from '~/common/utils/date-utils'
import { useCreateNewCrop } from './use-create-new-crop'
import { useUpdateExistingCrop } from './use-update-existing-crop'
import { CROP_TASK_TYPES } from '../constants/crop-modal-constants'

export const useCropModalHandlers = ({
  cropArchetypes,
  propagationCodes,
  effectiveTaskCodes,
  effectivePhaseCodes,
  cropPhaseTypes,
  previewScheduleData,
  frostDates,
  generateTaskFromCurrentPhases,
  defaultTasks,
}) => {
  const {
    archetype,
    species,
    variety,
    count,
    growthType,
    lifecycleType,
    notes,
    taskNotes,
    userSelectedOrgId,
    adjustedPhases,
    adjustedTasks,
    selectedTaskCodes,
    showNotes,
    successions,
    cropId,
    onClose,
    bulkAdd,
    newCropsQueue,
    isCustomCrop,
    customSpeciesName,
    isCustomVariety,
    customVarietyName,
    customFamilyCode,
  } = useCropModalContext()

  const dispatch = useCropModalContextDispatch()

  // Build crop body for update hook (still needs it at init)
  const cropBody = {
    species,
    variety,
    archetype,
    count,
    growthType,
    lifecycleType,
    notes,
    orgId: userSelectedOrgId,
    propagationCodes,
    successions,
    firstFrostDate: frostDates?.ffd ?? null,
  }

  const { createNewCrop } = useCreateNewCrop()
  const { updateExistingCrop } = useUpdateExistingCrop(cropBody)

  const handleSpeciesChange = useCallback((event, newValue) => {
    const newArchetype = newValue
      ? cropArchetypes.find(({ code }) => code === newValue.archetype_code) ?? null
      : null
    dispatch(setSpecies({ species: newValue ?? null, archetype: newArchetype }))
  }, [cropArchetypes, dispatch])

  const handleVarietyChange = useCallback((event, newValue) => {
    dispatch(setVariety({ variety: newValue ?? null }))
  }, [dispatch])

  const handleCountChange = useCallback((val) => {
    dispatch(setCount({ count: val }))
  }, [dispatch])

  const handleCollectionToggle = useCallback((code, isSelected) => {
    if (!isSelected) {
      dispatch(setSelectedCollections({ selectedCollections: [code] })) // Handled in CropSpeciesField
    }
  }, [dispatch])

  const handlePhaseToggle = useCallback((phaseCode) => {
    dispatch(togglePhaseCode({ phaseCode, effectivePhaseCodes }))

    const isAdding = !effectivePhaseCodes?.includes(phaseCode)
    const currentPhases = adjustedPhases ?? previewScheduleData?.[0]?.phases ?? []

    let updatedPhases
    if (phaseCode === 'stratification') {
      updatedPhases = handleStratificationToggle(currentPhases, isAdding, cropPhaseTypes)
    } else if (phaseCode === 'decomposition') {
      updatedPhases = handleDecompositionToggle(currentPhases, isAdding, cropPhaseTypes)
    } else if (phaseCode === 'dormant') {
      updatedPhases = handleDormantToggle(currentPhases, isAdding, cropPhaseTypes)
    } else {
      updatedPhases = computePhasesAfterToggle(currentPhases, phaseCode, isAdding, cropPhaseTypes)
    }

    dispatch(setAdjustedPhases({ adjustedPhases: updatedPhases }))
    // Clear adjustedTasks so they regenerate from new phase positions via defaultTasks
    dispatch(setAdjustedTasks({ adjustedTasks: null }))

    // When removing a phase, also deselect its linked task
    if (!isAdding) {
      const linkedTaskCode = CROP_TASK_TYPES.find(t => t.requiredPhase === phaseCode)?.code
      if (linkedTaskCode && effectiveTaskCodes?.includes(linkedTaskCode)) {
        const currentTaskCodes = selectedTaskCodes ?? effectiveTaskCodes ?? []
        dispatch({ type: 'set-selected-task-codes', selectedTaskCodes: currentTaskCodes.filter(c => c !== linkedTaskCode) })
      }
    }
  }, [dispatch, effectivePhaseCodes, effectiveTaskCodes, selectedTaskCodes, adjustedPhases, previewScheduleData, cropPhaseTypes])

  const handleTaskToggle = useCallback((taskCode) => {
    const isAdding = !effectiveTaskCodes?.includes(taskCode)
    const currentTasks = adjustedTasks ?? defaultTasks ?? []

    let newTasks
    if (isAdding) {
      const newTask = generateTaskFromCurrentPhases?.(taskCode)
      if (newTask) {
        const taskOrder = CROP_TASK_TYPES.map(t => t.code)
        newTasks = _.sortBy([...currentTasks, newTask], t => taskOrder.indexOf(t.task_code))
      } else {
        newTasks = currentTasks
      }
    } else {
      newTasks = currentTasks.filter(t => t.task_code !== taskCode)
    }

    dispatch(toggleTaskCode({ taskCode, effectiveTaskCodes }))
    dispatch(setAdjustedTasks({ adjustedTasks: newTasks }))
  }, [dispatch, effectiveTaskCodes, adjustedTasks, defaultTasks, generateTaskFromCurrentPhases])

  const handlePhaseAdjust = useCallback((updatedPhases) => {
    const oldPhases = adjustedPhases ?? previewScheduleData?.[0]?.phases ?? []

    dispatch(setAdjustedPhases({ adjustedPhases: updatedPhases }))

    // Sync DTM from maturing phase duration
    const maturingPhase = updatedPhases.find(p => p.phase_type_code === 'maturing')
    if (maturingPhase) {
      const newDtm = parseIntervalToDays(maturingPhase.est_duration)
      dispatch({ type: 'set-dtm', dtm: newDtm })
    }

    // Sync indoor offset from germination + seedling duration
    const germinationPhase = updatedPhases.find(p => p.phase_type_code === 'germination')
    const seedlingPhase = updatedPhases.find(p => p.phase_type_code === 'seedling')
    if (germinationPhase && seedlingPhase && archetype?.indoor_sow_offset !== undefined) {
      const totalIndoorDays = parseIntervalToDays(germinationPhase.est_duration) + parseIntervalToDays(seedlingPhase.est_duration)
      const newIndoorWeeks = Math.round(totalIndoorDays / 7)
      const originalIndoorWeeks = Math.abs(archetype.indoor_sow_offset)
      const weeksDelta = newIndoorWeeks - originalIndoorWeeks
      dispatch({ type: 'set-indoor-offset', indoorOffset: (archetype.indoor_sow_offset || 0) - weeksDelta })
    }

    // Sync tasks to reflect their linked phase's new position/size
    const currentTasks = previewScheduleData?.[0]?.tasks ?? []
    if (currentTasks.length > 0) {
      const maturePhase = updatedPhases.find(p => p.phase_type_code === 'mature')

      const newTasks = currentTasks.map(task => {
        const taskType = CROP_TASK_TYPES.find(t => t.code === task.task_code)
        if (!taskType?.requiredPhase) return task

        const oldPhase = oldPhases.find(p => p.phase_type_code === taskType.requiredPhase)
        const newPhase = updatedPhases.find(p => p.phase_type_code === taskType.requiredPhase)
        if (!oldPhase || !newPhase) return task

        // Harvest task: always mirror the mature phase exactly (start AND end/duration).
        // This covers both start-handle drags (position change) and end-handle drags (size change).
        // User can override by dragging the harvest window directly; the next phase drag will re-sync.
        if (task.task_code === 'harvest' && maturePhase) {
          const matureDays = parseIntervalToDays(maturePhase.est_duration)
          const taskEnd = new Date(new Date(maturePhase.est_started_at).getTime() + matureDays * 86400000).toISOString()
          return {
            ...task,
            task_window_start: maturePhase.est_started_at,
            duration: maturePhase.est_duration,
            task_window_end: taskEnd,
          }
        }

        // All other tasks: shift by the same delta as their linked phase's start date
        const deltaMs = new Date(newPhase.est_started_at).getTime() - new Date(oldPhase.est_started_at).getTime()
        if (deltaMs === 0) return task

        return {
          ...task,
          task_window_start: task.task_window_start
            ? new Date(new Date(task.task_window_start).getTime() + deltaMs).toISOString()
            : task.task_window_start,
          task_window_end: task.task_window_end
            ? new Date(new Date(task.task_window_end).getTime() + deltaMs).toISOString()
            : task.task_window_end,
        }
      })
      dispatch(setAdjustedTasks({ adjustedTasks: newTasks }))
    }
  }, [dispatch, archetype, adjustedPhases, previewScheduleData])

  const handleTaskAdjust = useCallback((updatedTasks) => {
    dispatch(setAdjustedTasks({ adjustedTasks: updatedTasks }))
  }, [dispatch])

  const handleGrowthTypeChange = useCallback((newGrowthType) => {
    dispatch(setGrowthType({ growthType: newGrowthType }))

    const currentPhases = adjustedPhases ?? previewScheduleData?.[0]?.phases
    if (!currentPhases || !archetype || !frostDates?.ffd) return

    const maturePhaseIndex = currentPhases.findIndex(p => p.phase_type_code === 'mature')
    if (maturePhaseIndex === -1) return

    const maturePhase = currentPhases[maturePhaseIndex]
    const matureStart = new Date(maturePhase.est_started_at)
    const firstFrostDate = frostDates.ffd

    let newMatureDays
    if (newGrowthType === 'indeterminate') {
      newMatureDays = Math.max(1, Math.floor((firstFrostDate - matureStart) / (1000 * 60 * 60 * 24)))
    } else {
      newMatureDays = Math.max(1, (archetype.harvest_window ?? 3) * 7)
    }

    const newMatureEnd = new Date(matureStart)
    newMatureEnd.setDate(newMatureEnd.getDate() + newMatureDays)

    const updatedPhases = currentPhases.map((phase, idx) => {
      if (phase.phase_type_code === 'mature') {
        return { ...phase, est_duration: `${newMatureDays} days` }
      }
      if (idx > maturePhaseIndex) {
        let cumulativeStart = new Date(newMatureEnd)
        for (let i = maturePhaseIndex + 1; i < idx; i++) {
          const prevDuration = parseIntervalToDays(currentPhases[i].est_duration)
          cumulativeStart.setDate(cumulativeStart.getDate() + prevDuration)
        }
        return { ...phase, est_started_at: cumulativeStart.toISOString() }
      }
      return phase
    })

    dispatch(setAdjustedPhases({ adjustedPhases: updatedPhases }))
  }, [dispatch, adjustedPhases, previewScheduleData, archetype, frostDates])

  const handleLifecycleTypeChange = useCallback((newLifecycleType) => {
    dispatch(setLifecycleType({ lifecycleType: newLifecycleType }))
  }, [dispatch])

  const handleResetPositions = useCallback(() => {
    dispatch(setAdjustedPhases({ adjustedPhases: null }))
    dispatch(setAdjustedTasks({ adjustedTasks: null }))
  }, [dispatch])

  const handleResetWithPhases = useCallback(() => {
    dispatch(setAdjustedPhases({ adjustedPhases: null }))
    dispatch(setAdjustedTasks({ adjustedTasks: null }))
    dispatch({ type: 'set-selected-phase-codes', selectedPhaseCodes: null })
    dispatch({ type: 'set-selected-task-codes', selectedTaskCodes: null })
    // Reset DTM/offsets to archetype defaults
    if (archetype) {
      dispatch({ type: 'set-indoor-offset', indoorOffset: archetype.indoor_sow_offset })
      dispatch({ type: 'set-outdoor-offset', outdoorOffset: archetype.outdoor_move_offset })
      dispatch({ type: 'set-dtm', dtm: species?.dtm ?? archetype?.dtm })
    }
  }, [dispatch, archetype, species])

  const handleCustomCropToggle = useCallback((enabled) => {
    dispatch(setCustomCropMode({ isCustomCrop: enabled }))
  }, [dispatch])

  const handleCustomSpeciesNameChange = useCallback((name) => {
    dispatch(setCustomSpeciesName({ customSpeciesName: name }))
  }, [dispatch])

  const handleCustomVarietyToggle = useCallback((enabled) => {
    dispatch(setCustomVarietyMode({ isCustomVariety: enabled }))
  }, [dispatch])

  const handleCustomVarietyNameChange = useCallback((name) => {
    dispatch(setCustomVarietyName({ customVarietyName: name }))
  }, [dispatch])

  const handleCustomFamilyChange = useCallback((_event, newValue) => {
    dispatch(setCustomFamilyCode({ customFamilyCode: newValue?.code ?? null }))
  }, [dispatch])

  const handleCustomArchetypeChange = useCallback((_event, newValue) => {
    dispatch(setArchetype({ archetype: newValue ?? null }))
  }, [dispatch])

  const handleSuccessionChange = useCallback((newSuccessions) => {
    dispatch(setSuccessions({ successions: newSuccessions }))
  }, [dispatch])

  const handleOrgSelect = useCallback((orgId, orgName) => {
    dispatch(setUserOrg({ orgId, orgName }))
  }, [dispatch])

  const handleToggleNotes = useCallback(() => {
    dispatch(setShowNotes({ showNotes: !showNotes }))
  }, [dispatch, showNotes])

  const _onClose = useCallback(() => {
    dispatch(resetCropModal())
    onClose?.()
  }, [dispatch, onClose])

  const onSubmit = useCallback(async () => {
    if (cropId) {
      await updateExistingCrop({
        cropId,
        phases: adjustedPhases ?? previewScheduleData[0]?.phases ?? [],
        tasks: adjustedTasks ?? previewScheduleData[0]?.tasks ?? [],
      })
      _onClose()
      return
    }

    if (isCustomCrop && !customSpeciesName?.trim()) return

    const allTaskCodes = CROP_TASK_TYPES.map(t => t.code)
    const skipTaskTypes = allTaskCodes.filter(code => !effectiveTaskCodes?.includes(code))
    const rawTasks = adjustedTasks ?? previewScheduleData[0]?.tasks ?? []
    const tasksWithNotes = rawTasks.map(task => {
      const customNote = taskNotes?.[task.task_code]?.trim()
      return customNote ? { ...task, notes: customNote } : task
    })
    const currentEntry = {
      species, variety, archetype, count, growthType, lifecycleType,
      notes, orgId: userSelectedOrgId, propagationCodes, successions,
      firstFrostDate: frostDates?.ffd ?? null,
      phases: adjustedPhases ?? previewScheduleData[0]?.phases ?? [],
      tasks: tasksWithNotes,
      skipTaskTypes,
      isCustomCrop,
      customSpeciesName,
      isCustomVariety,
      customVarietyName,
      customFamilyCode,
    }

    if (bulkAdd) {
      dispatch(enqueueCrop({ cropEntry: currentEntry }))
      dispatch(resetForm())
      return
    }

    for (const entry of [...newCropsQueue, currentEntry]) {
      await createNewCrop(entry)
    }
    _onClose()
  }, [
    _onClose, adjustedPhases, adjustedTasks, archetype, bulkAdd, count, createNewCrop,
    cropId, customFamilyCode, customSpeciesName, customVarietyName, dispatch,
    effectiveTaskCodes, frostDates, growthType, isCustomCrop, isCustomVariety,
    lifecycleType, newCropsQueue, notes, taskNotes, previewScheduleData, propagationCodes,
    species, successions, updateExistingCrop, userSelectedOrgId, variety,
  ])

  const onSubmitQueueOnly = useCallback(async () => {
    for (const entry of newCropsQueue) {
      await createNewCrop(entry)
    }
    _onClose()
  }, [_onClose, createNewCrop, newCropsQueue])

  return {
    handleSpeciesChange,
    handleVarietyChange,
    handleCountChange,
    handleCollectionToggle,
    handlePhaseToggle,
    handleTaskToggle,
    handlePhaseAdjust,
    handleTaskAdjust,
    handleResetPositions,
    handleGrowthTypeChange,
    handleLifecycleTypeChange,
    handleResetWithPhases,
    handleSuccessionChange,
    handleOrgSelect,
    handleToggleNotes,
    handleCustomCropToggle,
    handleCustomSpeciesNameChange,
    handleCustomVarietyToggle,
    handleCustomVarietyNameChange,
    handleCustomFamilyChange,
    handleCustomArchetypeChange,
    _onClose,
    onSubmit,
    onSubmitQueueOnly,
  }
}
